import { usDateFormat } from "@/utils/datetime";

import { PregnancyObservations } from "./hooks";

export type PregnancyDataType = {
  gestation: {
    weeks?: number;
    days?: number;
  };
  parity: number;
  edd: { display: string; value: string | undefined };
  gravidity: number;
  term: number;
  preterm: number;
  abortions: number;
  living: number;
  pregnant: boolean;
  lastMenstrualPeriod: string | undefined;
  // inLabor : boolean;
  // birthDate: string;
};

/**
 * Maps the fetched PregnancyObservations into a normalized PregnancyDataType.
 *
 * Pregnancy status, EDD, LMP, and gestational age are sourced exclusively from
 * `activePregnancyRecord` (managed by app/pregnancy; GA is pre-computed there).
 * GTPAL figures come from past-pregnancy FHIR observations.
 *
 * @param observations - Fetched observations and active pregnancy record
 * @returns Normalized pregnancy data object
 */
export default async function getPregnancyData(
  observations: PregnancyObservations,
): Promise<PregnancyDataType> {
  const {
    activePregnancyRecord,
    parityObservation,
    totalNumberOfPregnanciesObservation,
    numberOfLivingChildrenObservation,
    numberOfMiscarriagesObservation,
    numberOfAbortionsObservation,
    numberOfPrematureBirthsObservation,
    numberOfTermPregnanciesObservation,
    numberOfTotalAbortionsObservation,
  } = observations;

  const pregnant = !!activePregnancyRecord?.isActive;
  const parity = parityObservation?.valueInteger || 0;

  // EDD, LMP, and GA come exclusively from the PregnancyRecord (app/pregnancy).
  const eddString = activePregnancyRecord?.edd;
  const edd = eddString
    ? { display: usDateFormat(eddString), value: eddString }
    : { display: "--", value: undefined };

  // gestationalAge is pre-computed by buildPregnancyRecord via calculateGestationalAgeInWeeksAndDays.
  const gestation = activePregnancyRecord?.gestationalAge
    ? {
        weeks: activePregnancyRecord.gestationalAge.weeks,
        days: activePregnancyRecord.gestationalAge.days,
      }
    : { weeks: undefined, days: undefined };

  const lastMenstrualPeriod = activePregnancyRecord?.lastMenstrualPeriod;

  const totalNumberOfPregnancies = totalNumberOfPregnanciesObservation?.valueInteger || 0;
  const term = numberOfTermPregnanciesObservation?.valueInteger || 0;
  const preterm = numberOfPrematureBirthsObservation?.valueInteger || 0;
  const miscarriages = numberOfMiscarriagesObservation?.valueInteger || 0;
  const inducedAbortions = numberOfAbortionsObservation?.valueInteger || 0;
  const totalAbortions = numberOfTotalAbortionsObservation?.valueInteger;
  const abortions = totalAbortions !== undefined ? totalAbortions : miscarriages + inducedAbortions;

  const living = numberOfLivingChildrenObservation?.valueInteger || 0;

  return {
    gestation,
    pregnant,
    parity,
    edd,
    gravidity: totalNumberOfPregnancies,
    term,
    preterm,
    abortions,
    living,
    lastMenstrualPeriod,
    // ToDo:
    // inLabor,
    // birthDate,
  };
}
